import React from 'react';
import { ArrowLeft, Clock, Activity, Users, Star, CheckCircle2, Dumbbell } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { gymData } from '../../mockDemos';
import { useNavigate } from 'react-router-dom';

export const GymDemo = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Dumbbell className="text-orange-500" size={32} />
              <span className="text-2xl font-bold text-gray-900">{gymData.name}</span>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-orange-500 text-orange-500 hover:bg-orange-50"
            >
              <ArrowLeft className="mr-2" size={16} />
              Back to Portfolio
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 to-white">
        <div className="max-w-7xl mx-auto text-center space-y-6">
          <Badge className="bg-orange-500 text-white hover:bg-orange-600">
            Demo Website by Pranshik
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900">
            {gymData.hero.headline}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {gymData.hero.subtext}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white">
              Join Now
            </Button>
            <Button size="lg" variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-50">
              Free Trial
            </Button>
          </div>
        </div>
      </section>

      {/* Classes Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl font-bold text-gray-900">Our Classes</h2>
            <p className="text-xl text-gray-600">Find the perfect class for your fitness goals</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gymData.classes.map((classItem) => (
              <Card key={classItem.id} className="border-2 hover:border-orange-500 transition-all duration-300 hover:shadow-lg">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{classItem.name}</CardTitle>
                      <CardDescription className="mt-2">
                        <div className="flex items-center space-x-2 text-sm">
                          <Clock size={16} />
                          <span>{classItem.duration}</span>
                        </div>
                      </CardDescription>
                    </div>
                    <Badge variant="outline" className="text-orange-600 border-orange-200">
                      {classItem.difficulty}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Time:</span>
                      <span className="font-semibold">{classItem.time}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Instructor:</span>
                      <span className="font-semibold">{classItem.instructor}</span>
                    </div>
                    <Button className="w-full mt-4 bg-orange-500 hover:bg-orange-600 text-white">
                      Book Class
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trainers Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl font-bold text-gray-900">Expert Trainers</h2>
            <p className="text-xl text-gray-600">Train with certified professionals</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {gymData.trainers.map((trainer) => (
              <Card key={trainer.id} className="text-center border-2 hover:border-orange-500 transition-all duration-300">
                <CardHeader>
                  <Avatar className="w-32 h-32 mx-auto mb-4">
                    <AvatarImage src={trainer.avatar} alt={trainer.name} />
                    <AvatarFallback>{trainer.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <CardTitle className="text-2xl">{trainer.name}</CardTitle>
                  <CardDescription className="text-base">{trainer.specialty}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center space-x-2 text-orange-500">
                    <Activity size={20} />
                    <span className="font-semibold">{trainer.experience} Experience</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Plans */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl font-bold text-gray-900">Membership Plans</h2>
            <p className="text-xl text-gray-600">Choose the plan that works for you</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {gymData.memberships.map((plan) => (
              <Card
                key={plan.id}
                className={`border-2 transition-all duration-300 ${
                  plan.popular ? 'border-orange-500 shadow-xl scale-105' : 'hover:border-orange-500'
                }`}
              >
                <CardHeader className="text-center">
                  {plan.popular && (
                    <Badge className="mx-auto mb-2 bg-orange-500 text-white">Most Popular</Badge>
                  )}
                  <CardTitle className="text-3xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-orange-500">{plan.price}</span>
                    <span className="text-gray-600">/{plan.period}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <CheckCircle2 className="text-orange-500" size={18} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full mt-6 ${
                      plan.popular
                        ? 'bg-orange-500 hover:bg-orange-600 text-white'
                        : 'border-orange-500 text-orange-500 hover:bg-orange-50'
                    }`}
                    variant={plan.popular ? 'default' : 'outline'}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto text-center space-y-4">
          <div className="flex items-center justify-center space-x-3">
            <Dumbbell size={28} />
            <span className="text-2xl font-bold">{gymData.name}</span>
          </div>
          <p className="text-gray-400">{gymData.tagline}</p>
          <div className="pt-4 border-t border-gray-800">
            <p className="text-sm text-gray-500">
              This is a demo website created by <span className="text-orange-500 font-semibold">Pranshik</span>
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Want a website like this? <a href="/" className="text-orange-500 hover:underline">Contact me</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
